import { useState } from 'react'

import { Alert, Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import { FieldArray, FormikProvider } from 'formik'
import {
  AtsApplicationFillEvaluationFormFragment,
  AtsFillEvaluationFormDocument,
  AtsFillEvaluationFormMutation,
  AtsJobApplicationNotesDocument,
  EvaluationFormListItemFragment,
} from 'generated/resources/graphql'
import { sumBy } from 'lodash'

import TextEditor from 'modules/core/components/TextEditor'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { VisibilityToggler } from 'modules/ats/components/Feedbacks/VisibilityToggler'
import { calculateAverageRating } from 'modules/ats/lib/utils/evaluationForms'
import {
  useNotesMentions,
  useRichTextEditorNotesMentions,
} from 'modules/ats/lib/utils/useNotesMentions'

import { AnswerItem } from './AnswerItem'
import { AtsEvaluationFormValues } from './forms'

interface Props {
  evaluationForm: EvaluationFormListItemFragment
  application: AtsApplicationFillEvaluationFormFragment
  onSubmitFillEvaluationForm: () => void
  restrictedCommentsAvailable?: boolean
}

export const FillEvaluationForm = ({
  evaluationForm,
  application,
  onSubmitFillEvaluationForm,
  restrictedCommentsAvailable,
}: Props) => {
  const [createFeedback, { loading }] = useMutation(AtsFillEvaluationFormDocument, {
    extractorFn: (data: AtsFillEvaluationFormMutation) => data.atsMutations.createAtsFeedback,
    refetchQueries: [AtsJobApplicationNotesDocument],
  })
  const { access } = useCurrentSession()
  const [accessesIds, setAccessesIds] = useState<string[] | null>([])
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()
  const uniqueMentioningAccesses = useNotesMentions({
    atsJobPostingId: application.jobPosting.id,
    candidateId: application.candidate.id,
  })
  const {
    accesses: uniqueRichTextEditorMentioningAccesses,
    loading: richTextEditorMentionsLoading,
  } = useRichTextEditorNotesMentions({
    atsJobPostingId: application.jobPosting.id,
    candidateId: application.candidate.id,
  })

  const parsedAnswers = evaluationForm.questions.reduce(
    (all, question) => ({
      ...all,
      [question.id]: { questionId: question.id, score: 0, note: '' },
    }),
    {}
  )

  const form = useForm<AtsEvaluationFormValues>({
    defaultValues: {
      atsCandidateId: application.candidate.id,
      evaluationFormAnswers: parsedAnswers || {},
      attachments: [],
      atsApplicationId: application.id,
      atsApplicationPhaseId: application.applicationPhase.id,
      atsEvaluationFormsId: evaluationForm.id,
      description: '',
      rating: null,
      accessId: access.id,
      mentionIds: accessesIds,
      visibility: 'public',
    },
    async onSubmit(data, { setErrors }) {
      const answers = Object.values(data.evaluationFormAnswers)

      const result = await createFeedback({
        variables: {
          ...data,
          evaluationFormAnswers: answers,
          rating: calculateAverageRating(answers.map((answer) => answer.score)),
          mentionIds: accessesIds,
        },
      })
      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.evaluation_forms.new.success_message'),
        })
      }
      form.resetForm()
      onSubmitFillEvaluationForm()
      return handled
    },
  })

  const showValidationWarning =
    sumBy(Object.values(form.values.evaluationFormAnswers), (answer) => answer.score) === 0

  return (
    <FormikProvider value={form}>
      <Form onSubmit={form.handleSubmit} padding="s0">
        <Box gap="s16">
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
            borderRadius={{ all: 'abs008' }}
            background="grey000White"
            gapDivider
          >
            <FieldArray
              name="answers"
              render={() =>
                evaluationForm.questions.map((question) => (
                  <AnswerItem key={question.id} question={question} />
                ))
              }
            />
          </Box>
          <Box gap="s8">
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
              <Text.Normal color="grey1000" weight="medium">
                {i18n.t('ats.application_details.evaluations.summary')}
              </Text.Normal>
              {restrictedCommentsAvailable && (
                <VisibilityToggler
                  value={form.values.visibility || 'public'}
                  onChange={(value) => form.setFieldValue('visibility', value)}
                />
              )}
            </Box>
            {richTextEditorLoading || richTextEditorMentionsLoading ? (
              <RichTextEditor.Skeleton rows={1} />
            ) : canUseRichTextEditor ? (
              <RichTextEditor
                {...editorConfig}
                title={evaluationForm?.name}
                onChange={(data) => {
                  form.setFieldValue('description', data?.value)
                  setAccessesIds(data?.mentionIds?.map(String) ?? null)
                }}
                placeholder={i18n.t('ats.application_details.evaluations.description_placeholder')}
                mentionsConfig={{
                  users: uniqueRichTextEditorMentioningAccesses,
                }}
                initialEditorState={{ content: form.values.description ?? '' }}
              />
            ) : (
              <TextEditor
                onChange={(data) => {
                  if (data && typeof data !== 'string') {
                    form.setFieldValue('description', data.value)
                    setAccessesIds(data?.ids?.map(String) ?? null)
                  }
                }}
                placeholder={i18n.t('ats.application_details.evaluations.description_placeholder')}
                initialEditorState={{ data: form.values.description }}
                height="s200"
                hasMentions
                users={uniqueMentioningAccesses}
                enableBetaFeatures={false}
              />
            )}
          </Box>

          <Box gap="s16">
            {showValidationWarning && (
              <Alert color="warning">
                {i18n.t('ats.application_details.evaluations.validation_warning')}
              </Alert>
            )}

            <Box flexDirection="row" justifyContent="flexEnd" gap="s16">
              <Button
                type="secondary"
                onClick={() => {
                  onSubmitFillEvaluationForm()
                  form.resetForm()
                }}
              >
                {i18n.t('common.cancel')}
              </Button>
              <Button disabled={loading} submit>
                {i18n.t('common.submit')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Form>
    </FormikProvider>
  )
}
